/**
 * Vendor → Payments tab
 * Shows all outgoing payments made to this vendor.
 * CHECK-method payments get a "Print check" button that opens the PDF in a new tab.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { paymentsListQueryOptions } from "@/lib/queries";
import { getToken } from "@/lib/api";
import type { Payment } from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/payments")({
  component: VendorPaymentsTab,
});

const METHOD_LABEL: Record<string, string> = {
  check: "Check",
  ach: "ACH",
  wire: "Wire",
  cash: "Cash",
  credit_card: "Credit card",
  other: "Other",
};

function fmt(n: string | number) {
  const v = parseFloat(String(n));
  return isNaN(v)
    ? "—"
    : v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 2 });
}

/** Open the check PDF in a new browser tab.
 *  We need to pass the auth token, so we fetch as a blob and use createObjectURL. */
async function openCheckPdf(paymentId: string) {
  const token = getToken();
  const res = await fetch(`/api/payments/${paymentId}/check.pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    alert(`Could not generate check PDF: ${body.detail ?? res.statusText}`);
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

function MethodBadge({ method }: { method: string }) {
  const isCheck = method === "check";
  const base =
    "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium";
  const cls = isCheck
    ? `${base} bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300`
    : `${base} bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-400`;
  return <span className={cls}>{isCheck ? "🖨 " : ""}{METHOD_LABEL[method] ?? method}</span>;
}

function VendorPaymentsTab() {
  const { vendorId } = Route.useParams();
  const paymentsQ = useQuery(
    paymentsListQueryOptions({ direction: "outgoing", party_id: vendorId }),
  );
  const payments: Payment[] = paymentsQ.data ?? [];

  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount), 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Payments
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {payments.length} payment(s) ·{" "}
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {fmt(totalPaid)} total paid
            </span>
          </p>
        </div>
      </div>

      {paymentsQ.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}
      {paymentsQ.error && (
        <div className="text-sm text-red-600">
          Failed to load: {(paymentsQ.error as Error).message}
        </div>
      )}

      {!paymentsQ.isLoading && payments.length === 0 && (
        <div className="text-sm text-slate-400 dark:text-slate-500">
          No payments recorded for this vendor.
        </div>
      )}

      {payments.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5">Payment #</th>
                <th className="text-left px-3 py-2.5">Date</th>
                <th className="text-left px-3 py-2.5">Method</th>
                <th className="text-left px-3 py-2.5">Check #</th>
                <th className="text-left px-3 py-2.5">Reference</th>
                <th className="text-left px-3 py-2.5">Memo</th>
                <th className="text-right px-3 py-2.5">Amount</th>
                <th className="text-right px-3 py-2.5">Applications</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {payments
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime(),
                )
                .map((pmt) => (
                  <tr
                    key={pmt.id}
                    className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-3 py-2.5 font-mono text-slate-800 dark:text-slate-200">
                      {pmt.payment_no}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400">
                      {pmt.payment_date}
                    </td>
                    <td className="px-3 py-2.5">
                      <MethodBadge method={pmt.method} />
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500 dark:text-slate-400">
                      {(pmt as any).check_no ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400 font-mono text-xs">
                      {pmt.reference ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                      {pmt.memo ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-900 dark:text-slate-100">
                      {fmt(pmt.amount)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-slate-500 dark:text-slate-400">
                      {pmt.applications.length} line(s)
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {pmt.method === "check" && (
                        <button
                          onClick={() => openCheckPdf(pmt.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-violet-600 hover:bg-violet-700 text-white transition-colors"
                          title="Print check PDF"
                        >
                          🖨 Print check
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 dark:bg-gray-900 border-t border-slate-200 dark:border-gray-700">
                <td
                  colSpan={6}
                  className="px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400"
                >
                  Total
                </td>
                <td className="px-3 py-2 text-right font-bold text-slate-900 dark:text-slate-100">
                  {fmt(totalPaid)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
